import moment from "moment-timezone";

// Define the input types
interface Scope {
  read: boolean;
  write: boolean;
  delete: boolean;
}

interface Subresource {
  resource: string;
  scopes: Scope;
}

interface Permission {
  resource: string;
  subresources: Subresource[];
}

interface RolePermissionInput {
  role: string;
  userCount: number;
  permissions: Permission[];
}

// Define the output types
export interface ProcessedPermissions {
  roles: string[];
  roleUserCounts: Record<string, number>;
  permissionsData: Record<
    string, // role
    Record<
      string, // module
      Record<string, Scope> // subresource
    >
  >;
  modules: {
    name: string;
    subresources: string[];
  }[];
}

// Main preprocessing function
export function preprocessPermissionsData(
  rawData: RolePermissionInput[]
): ProcessedPermissions {
  const roles: string[] = [];
  const roleUserCounts: Record<string, number> = {};
  const permissionsMap: Record<
    string,
    Record<string, Record<string, Scope>>
  > = {};
  const moduleSet: Map<string, Set<string>> = new Map();

  for (const roleEntry of rawData) {
    const role = roleEntry.role;
    const userCount = roleEntry.userCount || 0;
    roles.push(role);
    roleUserCounts[role] = userCount;
    permissionsMap[role] = {};

    for (const perm of roleEntry.permissions) {
      const module = perm.resource || "Unknown Module";

      if (!moduleSet.has(module)) moduleSet.set(module, new Set());

      for (const sub of perm.subresources || []) {
        const subName = sub.resource || "Unknown Subresource";
        const scopes: Scope = sub.scopes || {
          read: false,
          write: false,
          delete: false,
        };

        moduleSet.get(module)!.add(subName);

        if (!permissionsMap[role][module]) {
          permissionsMap[role][module] = {};
        }

        permissionsMap[role][module][subName] = {
          read: scopes.read || false,
          write: scopes.write || false,
          delete: scopes.delete || false,
        };
      }
    }
  }

  const modules = Array.from(moduleSet.entries()).map(([name, subSet]) => ({
    name,
    subresources: Array.from(subSet),
  }));

  return {
    roles,
    roleUserCounts,
    permissionsData: permissionsMap,
    modules,
  };
}

type ScopeObject = {
  read: boolean;
  write: boolean;
  delete: boolean;
};

type Subresources = {
  id: number;
  resource: string;
  scopes: ScopeObject;
  subresources?: Subresource[]; // nested subresources support
};

type Permissions = {
  id: number;
  resource: string;
  subresources: Subresource[];
};

type RoleObject = {
  id: number;
  role: string;
  userCount: number;
  permissions: Permissions[];
};

export const setAllScopesToFalse = (roleData: RoleObject): RoleObject => {
  const updateSubresources = (subresources: Subresources[]): Subresources[] => {
    return subresources.map((sub: any) => ({
      ...sub,
      scopes: {
        read: false,
        write: false,
        delete: false,
      },
      ...(sub.subresources && {
        subresources: updateSubresources(sub.subresources),
      }),
    }));
  };

  const updatedPermissions = roleData.permissions.map((permission: any) => {
    const subresources = permission?.subresources || [];
    if (!subresources.length) {
      return {
        ...permission,
      };
    }
    return {
      ...permission,
      subresources: updateSubresources(subresources),
    };
  });

  return {
    ...roleData,
    permissions: updatedPermissions,
  };
};

type Scopes = {
  read: boolean;
  write: boolean;
  delete: boolean;
};

type Role = {
  id: number;
  role: string;
  userCount: number;
  permissions: Permission[];
};

type ChangeMap = {
  [role: string]: {
    [resource: string]: {
      [subresource: string]: Scopes;
    };
  };
};

export function applyScopeChangesToRoles(
  roles: Role[],
  changes: ChangeMap
): Role[] {
  return roles.map((role) => {
    const roleChanges = changes[role.role];
    if (!roleChanges) return role;

    const updatedPermissions = role.permissions.map((permission) => {
      const permissionChanges = roleChanges[permission.resource];
      if (!permissionChanges) return permission;
      if (!permission?.subresources?.length) {
        return {
          ...permission,
        };
      }

      const updatedSubresources = permission.subresources.map((sub) => {
        const newScope = permissionChanges[sub.resource];
        if (!newScope) return sub;

        return {
          ...sub,
          scopes: {
            ...sub.scopes,
            ...newScope,
          },
        };
      });

      return {
        ...permission,
        subresources: updatedSubresources,
      };
    });

    return {
      ...role,
      permissions: updatedPermissions,
    };
  });
}

export const formatLastUpdatedDate = (inputDate: string): string => {
  const date = moment.utc(inputDate).tz(moment.tz.guess());
  const now = moment().tz(moment.tz.guess());

  if (date.isSame(now, "day")) {
    return `Today at ${date.format("hh:mm A")}`;
  }

  if (date.isSame(now.clone().subtract(1, "day"), "day")) {
    return `Yesterday at ${date.format("hh:mm A")}`;
  }

  return date.format("MM/DD/YY [at] hh:mm A");
};
